/**
 * Floating-point slack for "does this block fit that zone" millimetre
 * comparisons.
 *
 * Laid-out zone dimensions come off split arithmetic that is never snapped
 * (`parent.w * ratio` and `parent.w - wA`, see packages/zpl-pipeline/src/split.ts),
 * so a zone the user reads as exactly 45 mm arrives here as 44.99999999999999:
 * a 100 mm template split 0.55 gives 55.00000000000001 / 44.99999999999999.
 * Comparing those raw floats made the designer REFUSE a 45 mm block from that
 * zone while every number in the rejection — which rounds for display — said it
 * fits: "needs 45×30 mm, slot is only 45×40 mm" (issue #126). Un-actionable:
 * there is nothing the user can shave off.
 *
 * 1e-6 mm is four orders of magnitude below a single 300-dpi dot (0.0847 mm),
 * so it can only ever swallow split residue — never a difference anyone typed.
 * A 0.04 mm shortfall (a 59.96 mm zone vs a 60 mm block) is 40 000× the
 * tolerance and still rejects.
 *
 * It is deliberately the SAME epsilon the pipeline's PLACEMENT_OVERFLOW lint
 * already applies to this exact question (packages/zpl-pipeline/src/lint-template.ts),
 * so the editor's drop gate and the lint agree on which placements are legal —
 * a tolerance any wider here would let users bake placements the linter then
 * flags as errors.
 */
export const MM_FIT_EPS = 1e-6

/**
 * True when `dims` fits inside `host`, tolerating split FP residue.
 *
 * Same subtraction form as the pipeline lint so the two stay comparable.
 */
export function fitsInMm(
  host: { widthMm: number; heightMm: number },
  dims: { widthMm: number; heightMm: number }
): boolean {
  return dims.widthMm - host.widthMm <= MM_FIT_EPS && dims.heightMm - host.heightMm <= MM_FIT_EPS
}
